/**
 * Just enough CSV for the batch screens: read a file the person picked, write
 * one back after they have edited it.
 *
 * Not a library. What arrives here is a spreadsheet export of at most a few
 * thousand short rows, and the whole grammar that matters is quoting: a field
 * may be wrapped in double quotes, inside which commas and newlines are
 * ordinary characters and `""` means one quote. That is the part hand-rolled
 * splitting on commas gets wrong, and it is the part addresses and names hit.
 */

/** A header as written, folded to the field it names. */
export const columnKey = (header: string): string =>
  header.trim().toLowerCase().replace(/[\s-]+/g, "_");

/** Rows of cells. The first row is the header; blank trailing lines are dropped. */
export function parseCsv(text: string): string[][] {
  // A BOM survives Excel and would otherwise become part of the first header.
  const input = text.replace(/^﻿/, "");
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;

  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    if (quoted) {
      if (char === '"') {
        // Doubled inside quotes is a literal quote; otherwise the field ends.
        if (input[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += char;
      continue;
    }
    if (char === '"') quoted = true;
    else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n" || char === "\r") {
      // A CRLF is one break, not two empty rows.
      if (char === "\r" && input[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += char;
  }
  if (cell !== "" || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  // Excel leaves a trailing newline, which is not a row of one empty field.
  return rows.filter((cells) => cells.some((value) => value !== ""));
}

/** Back to a file the API will read the same way it reads a template. */
export function toCsv(rows: string[][]): string {
  const cell = (value: string) =>
    /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  return rows.map((row) => row.map(cell).join(",")).join("\n");
}

/** Which of `required` the header does not name. Compared by field, not spelling. */
export function missingColumns(header: string[], required: string[]): string[] {
  const have = new Set(header.map(columnKey));
  return required.filter((column) => !have.has(columnKey(column)));
}
